import { useState, useEffect, useMemo, useCallback, useRef } from "react";

export interface UsePaginationOptions {
	initialPage?: number;
	initialRowsPerPage?: number;
	totalItems: number;
	onPageChange?: (page: number) => void;
	onRowsPerPageChange?: (rowsPerPage: number) => void;
}

export interface UsePaginationReturn {
	currentPage: number;
	endIndex: number;
	hasNextPage: boolean;
	hasPrevPage: boolean;
	rowsPerPage: number;
	startIndex: number;
	totalPages: number;
	nextPage: () => void;
	prevPage: () => void;
	reset: () => void;
	setCurrentPage: (page: number) => void;
	setRowsPerPage: (rows: number) => void;
}

export const usePagination = ({
	initialPage = 1,
	initialRowsPerPage = 10,
	totalItems,
	onPageChange,
	onRowsPerPageChange,
}: UsePaginationOptions): UsePaginationReturn => {
	// Input validation
	let validatedTotalItems = totalItems;
	let validatedInitialPage = initialPage;
	let validatedInitialRowsPerPage = initialRowsPerPage;

	if (totalItems < 0) {
		console.warn("[Pagination] totalItems cannot be negative, using 0");
		validatedTotalItems = 0;
	}

	if (initialRowsPerPage <= 0) {
		console.warn("[Pagination] rowsPerPage must be positive, using 10");
		validatedInitialRowsPerPage = 10;
	}

	if (initialPage < 1) {
		console.warn("[Pagination] initialPage must be >= 1, using 1");
		validatedInitialPage = 1;
	}

	const [currentPage, setCurrentPageState] = useState(validatedInitialPage);
	const [rowsPerPage, setRowsPerPageState] = useState(validatedInitialRowsPerPage);

	// Store callbacks in refs to avoid dependency issues
	const onPageChangeRef = useRef(onPageChange);
	const onRowsPerPageChangeRef = useRef(onRowsPerPageChange);

	// Update refs when callbacks change
	useEffect(() => {
		onPageChangeRef.current = onPageChange;
	}, [onPageChange]);

	useEffect(() => {
		onRowsPerPageChangeRef.current = onRowsPerPageChange;
	}, [onRowsPerPageChange]);

	// Calculate total pages
	const totalPages = useMemo(() => {
		return Math.ceil(validatedTotalItems / rowsPerPage);
	}, [validatedTotalItems, rowsPerPage]);

	// Calculate start and end indices for current page
	const startIndex = useMemo(() => {
		if (validatedTotalItems === 0) {
			return 0;
		}
		return (currentPage - 1) * rowsPerPage + 1;
	}, [currentPage, rowsPerPage, validatedTotalItems]);

	const endIndex = useMemo(() => {
		return Math.min(currentPage * rowsPerPage, validatedTotalItems);
	}, [currentPage, rowsPerPage, validatedTotalItems]);

	// Check if navigation is available - memoized for performance
	const hasNextPage = useMemo(() => currentPage < totalPages, [currentPage, totalPages]);
	const hasPrevPage = useMemo(() => currentPage > 1, [currentPage]);

	// Set current page with validation - wrapped in useCallback
	const setCurrentPage = useCallback(
		(page: number) => {
			if (page < 1) {
				return;
			}
			if (page > totalPages) {
				return;
			}

			setCurrentPageState(page);
			onPageChangeRef.current?.(page);
		},
		[totalPages]
	);

	// Set rows per page - wrapped in useCallback
	const setRowsPerPage = useCallback(
		(rows: number) => {
			setRowsPerPageState(rows);
			onRowsPerPageChangeRef.current?.(rows);

			// Reset to page 1 when rows per page changes
			const newTotalPages = Math.ceil(validatedTotalItems / rows);
			if (currentPage > newTotalPages) {
				setCurrentPageState(1);
				onPageChangeRef.current?.(1);
			}
		},
		[validatedTotalItems, currentPage]
	);

	// Navigation helpers - wrapped in useCallback
	const nextPage = useCallback(() => {
		if (hasNextPage) {
			setCurrentPage(currentPage + 1);
		}
	}, [hasNextPage, currentPage, setCurrentPage]);

	const prevPage = useCallback(() => {
		if (hasPrevPage) {
			setCurrentPage(currentPage - 1);
		}
	}, [hasPrevPage, currentPage, setCurrentPage]);

	// Reset to initial values - wrapped in useCallback
	const reset = useCallback(() => {
		setCurrentPageState(validatedInitialPage);
		setRowsPerPageState(validatedInitialRowsPerPage);
	}, [validatedInitialPage, validatedInitialRowsPerPage]);

	// Auto-reset to page 1 when totalItems changes and current page exceeds new total
	// Fixed: removed onPageChange from dependencies to prevent infinite loops
	useEffect(() => {
		if (currentPage > totalPages && totalPages > 0) {
			setCurrentPageState(1);
			onPageChangeRef.current?.(1);
		}
	}, [totalPages, currentPage]);

	return {
		currentPage,
		rowsPerPage,
		totalPages,
		startIndex,
		endIndex,
		hasNextPage,
		hasPrevPage,
		setCurrentPage,
		setRowsPerPage,
		nextPage,
		prevPage,
		reset,
	};
};
